import React, { useEffect, useRef, useState, memo, useCallback } from 'react';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';

// Import HLS support
import '@videojs/http-streaming';

// Add type definition for VHS tech without extending videojs.Tech
interface VHSTech {
  vhs?: {
    bandwidth: number;
    playlists?: {
      media(): any;
    };
    on(event: string, callback: (event?: any) => void): void;
  };
}

interface ProfessionalVideoPlayerProps {
  videoUrl: string;
  posterUrl?: string;
  autoplay?: boolean;
  muted?: boolean;
  loop?: boolean;
  className?: string;
  onReady?: (player: any) => void;
  onError?: (error: any) => void;
  adaptiveStreaming?: boolean; // Thêm prop để bật/tắt adaptive streaming
}

const ProfessionalVideoPlayer: React.FC<ProfessionalVideoPlayerProps> = memo(({
  videoUrl,
  posterUrl,
  autoplay = true,
  muted = true,
  loop = true,
  className = "",
  onReady,
  onError,
  adaptiveStreaming = true
}) => {
  const videoRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<any>(null);
  const [networkSpeed, setNetworkSpeed] = useState<string>('unknown');
  const hasInitiallyStarted = useRef(false);
  const mountedRef = useRef(true);

  // Network detection function
  const detectNetworkSpeed = useCallback((): number => {
    // Check for Network Information API
    if ('connection' in navigator) {
      const connection = (navigator as any).connection;
      if (connection) {
        const effectiveType = connection.effectiveType;
        const downlink = connection.downlink; // Mbps

        // Set bandwidth based on effective connection type
        switch (effectiveType) {
          case 'slow-2g':
            return 0.15 * 1024 * 1024; // 150 kbps
          case '2g':
            return 0.3 * 1024 * 1024; // 300 kbps
          case '3g':
            return 1.5 * 1024 * 1024; // 1.5 Mbps
          case '4g':
            return Math.min(downlink * 1024 * 1024, 10 * 1024 * 1024); // Max 10 Mbps
          default:
            return downlink ? downlink * 1024 * 1024 : 2 * 1024 * 1024;
        }
      }
    }

    // Fallback: detect based on user agent for mobile
    if (/Mobi|Android/i.test(navigator.userAgent)) {
      return 1024 * 1024; // 1 Mbps for mobile
    }

    return 2 * 1024 * 1024; // 2 Mbps default
  }, []);

  // Network monitoring
  useEffect(() => {
    if (!adaptiveStreaming) return;

    const updateNetworkInfo = () => {
      if ('connection' in navigator) {
        const connection = (navigator as any).connection;
        if (connection) {
          setNetworkSpeed(connection.effectiveType || 'unknown');

          // Update player bandwidth if available
          const player = playerRef.current;
          if (player && !player.isDisposed()) {
            try {
              const tech = player.tech() as VHSTech;
              if (tech && tech.vhs) {
                const newBandwidth = detectNetworkSpeed();
                tech.vhs.bandwidth = newBandwidth;
                console.log(`Network changed: ${connection.effectiveType}, Bandwidth set to: ${(newBandwidth / 1024 / 1024).toFixed(2)} Mbps`);
              }
            } catch (error) {
              console.warn('Could not update bandwidth:', error);
            }
          }
        }
      }
    };

    // Listen for network changes
    if ('connection' in navigator) {
      const connection = (navigator as any).connection;
      connection.addEventListener('change', updateNetworkInfo);

      // Initial network detection
      updateNetworkInfo();

      return () => {
        connection.removeEventListener('change', updateNetworkInfo);
      };
    }
  }, [adaptiveStreaming, detectNetworkSpeed]);

  // Memoize callbacks để tránh re-render
  const handleReady = useCallback((player: any) => {
    if (onReady && mountedRef.current) {
      onReady(player);
    }
  }, [onReady]);

  const handleError = useCallback((error: any) => {
    if (onError && mountedRef.current) {
      onError(error);
    }
  }, [onError]);

  // Helper function để determine video type
  const getVideoType = useCallback((url: string): string => {
    if (url.includes('.m3u8')) {
      return 'application/x-mpegURL'; // HLS
    } else if (url.includes('.mpd')) {
      return 'application/dash+xml'; // DASH
    } else if (url.includes('.mp4')) {
      return 'video/mp4';
    } else if (url.includes('.webm')) {
      return 'video/webm';
    }
    return 'video/mp4'; // Default fallback
  }, []);

  useEffect(() => {
    mountedRef.current = true;

    // Chỉ khởi tạo player một lần khi component mount
    if (!playerRef.current && videoRef.current) {
      hasInitiallyStarted.current = false;

      const videoElement = document.createElement('video-js');

      if (className) {
        videoElement.classList.add(...className.split(' '));
      }

      videoRef.current.appendChild(videoElement);

      // Detect initial network speed
      const initialBandwidth = adaptiveStreaming ? detectNetworkSpeed() : 2 * 1024 * 1024;

      const player = playerRef.current = videojs(videoElement, {
        autoplay: false, // Tắt autoplay của video.js, tự handle
        controls: false,
        responsive: true,
        fluid: true,
        muted: muted,
        loop: true,
        poster: posterUrl,
        preload: 'metadata',
        playsinline: true,
        html5: {
          vhs: {
            enableLowInitialPlaylist: true,
            smoothQualityChange: true,
            allowSeeksWithinUnsafeLiveWindow: true,
            handlePartialData: true,
            bandwidth: initialBandwidth,
            // Thêm cấu hình adaptive streaming
            withCredentials: false,
            overrideNative: true,
            experimentalBufferBasedABR: adaptiveStreaming,
            experimentalLLHLS: false,
            useBandwidthFromLocalStorage: adaptiveStreaming,
            // Cấu hình buffer để phù hợp với network
            maxPlaylistRetries: 3,
            segmentRequestTimeout: 10000,
            experimentalMaxMaxBandwidth: 10 * 1024 * 1024, // 10 Mbps max
          },
          hls: {
            enableLowInitialPlaylist: true,
            smoothQualityChange: true,
            withCredentials: false
          }
        },
        experimentalSvgIcons: false,
        sources: [{
          src: videoUrl,
          type: getVideoType(videoUrl)
        }],
        techOrder: ['html5'],
        breakpoints: {},
        textTrackDisplay: false,
        userActions: {
          hotkeys: false,
          click: false,
          doubleClick: false
        },
      }, () => {
        if (!mountedRef.current) return;

        handleReady(player);

        // Setup adaptive streaming event listeners
        if (adaptiveStreaming) {
          player.on('loadstart', () => {
            if (mountedRef.current) {
              console.log('Video loading started');
              // Log initial bandwidth
              const tech = player.tech() as VHSTech;
              if (tech && tech.vhs) {
                console.log(`Initial bandwidth: ${(tech.vhs.bandwidth / 1024 / 1024).toFixed(2)} Mbps`);
              }
            }
          });

          // Monitor bandwidth changes during playback
          player.on('loadedmetadata', () => {
            if (!mountedRef.current) return;

            const tech = player.tech() as VHSTech;
            if (tech && tech.vhs) {
              // Monitor playlist changes for adaptive streaming
              tech.vhs.on('bandwidthupdate', (event: any) => {
                if (mountedRef.current) {
                  console.log('Bandwidth updated:', (event.bandwidth / 1024 / 1024).toFixed(2), 'Mbps');
                }
              });
            }
          });

          // Handle quality changes
          player.on('resolutionchange', () => {
            if (mountedRef.current) {
              try {
                const tech = player.tech() as VHSTech;
                if (tech && tech.vhs && tech.vhs.playlists) {
                  const currentPlaylist = tech.vhs.playlists.media();
                  if (currentPlaylist && currentPlaylist.attributes) {
                    const resolution = currentPlaylist.attributes.RESOLUTION;
                    console.log('Quality changed to:', resolution ? `${resolution.width}x${resolution.height}` : 'auto');
                  }
                }
              } catch (error) {
                console.warn('Could not get resolution info:', error);
              }
            }
          });
        }

        // Chỉ autoplay một lần khi ready
        player.ready(() => {
          if (!mountedRef.current) return;

          console.log('Player is ready');
          if (autoplay && !hasInitiallyStarted.current) {
            hasInitiallyStarted.current = true;
            setTimeout(() => {
              if (mountedRef.current && player && !player.isDisposed()) {
                player.play()?.catch((error: any) => {
                  console.warn('Autoplay failed:', error);
                });
              }
            }, 100);
          }
        });

        // Đơn giản hóa event handlers
        player.on('ended', () => {
          if (!mountedRef.current || !loop) return;

          if (player && !player.isDisposed()) {
            player.currentTime(0);
            player.play()?.catch((error: any) => {
              console.warn('Loop play failed:', error);
            });
          }
        });

        player.on('error', (e: any) => {
          if (mountedRef.current) {
            console.error('Video error:', e);
            handleError(e);
          }
        });

        // Handle network errors and retry with lower quality
        player.on('loadeddata', () => {
          if (!mountedRef.current || !adaptiveStreaming) return;

          const tech = player.tech() as VHSTech;
          if (tech && tech.vhs) {
            // Set up error recovery
            tech.vhs.on('usage', (event: any) => {
              if (event.name === 'vhs-rendition-blacklisted') {
                console.log('Rendition blacklisted, switching to lower quality');
              }
            });
          }
        });
      });
    }

    // Cleanup function
    return () => {
      mountedRef.current = false;
    };
  }, [adaptiveStreaming, detectNetworkSpeed]); // Thêm dependencies

  // Separate useEffect để handle prop changes mà không re-initialize player
  useEffect(() => {
    const player = playerRef.current;
    if (player && !player.isDisposed()) {
      player.muted(muted);
    }
  }, [muted]);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
      const player = playerRef.current;
      if (player && !player.isDisposed()) {
        player.dispose();
        playerRef.current = null;
      }
    };
  }, []);

  return (
    <div
      data-vjs-player
      className="w-full h-full relative"
    >
      <div ref={videoRef} className="w-full h-full" />
      {/* Custom CSS để ẩn hoàn toàn các controls và cải thiện mobile */}
      <style>{`
        :global(.video-js .vjs-control-bar) {
          display: none !important;
        }
        :global(.video-js .vjs-big-play-button) {
          display: none !important;
        }
        :global(.video-js .vjs-loading-spinner) {
          display: none !important;
        }
        :global(.video-js .vjs-poster) {
          background-size: cover !important;
          background-position: center !important;
        }
        :global(.video-js) {
          width: 100% !important;
          height: 100% !important;
          object-fit: cover !important;
        }
        :global(.video-js video) {
          width: 100% !important;
          height: 100% !important;
          object-fit: cover !important;
        }
        
        /* Mobile optimizations */
        @media (max-width: 768px) {
          :global(.video-js) {
            position: absolute !important;
            top: 0 !important;
            left: 0 !important;
            width: 100% !important;
            height: 100% !important;
          }
          :global(.video-js video) {
            object-fit: cover !important;
            width: 100% !important;
            height: 100% !important;
          }
        }
        
        /* Desktop optimizations */
        @media (min-width: 769px) {
          :global(.video-js) {
            aspect-ratio: 16/9 !important;
          }
        }
      `}</style>
    </div>
  );
});

ProfessionalVideoPlayer.displayName = 'ProfessionalVideoPlayer';

export default ProfessionalVideoPlayer;
